import { MIME_TO_EXT } from "@/src/constants/files-extensions";
import { PickedFile } from "@/src/models/file.models";
import {
  AttachmentInput,
  MergedAttachment,
  PickedFileLoose,
} from "@/src/models/wiki.models";

export const KB_MIN_CONTENT_LENGTH = 100;
const KB_CONTENT_PADDING = "·".repeat(KB_MIN_CONTENT_LENGTH);

export const KB_SUPPORTED_MIME_TYPES = new Set([
  "application/pdf",
  "text/plain",
]);

const BLOCKS_UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const hasFileExtension = (name: string): boolean =>
  /\.[a-zA-Z0-9]{1,5}$/.test(name);

export const resolveFileName = (
  uri: string,
  fileName?: string | null,
  mimeType?: string | null,
): string => {
  const extFromMime = mimeType ? (MIME_TO_EXT[mimeType] ?? null) : null;

  if (fileName && hasFileExtension(fileName)) return fileName;
  if (fileName && extFromMime) return `${fileName}.${extFromMime}`;

  const uriSegment = uri.split("/").pop()?.split("?")[0] ?? "";
  if (uriSegment && hasFileExtension(uriSegment)) return uriSegment;

  return `upload_${Date.now()}.${extFromMime ?? "bin"}`;
};

export const stripHtmlAndNormalize = (content: string): string =>
  content
    .replace(/<[^>]*>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[^\S\n]{2,}/g, " ")
    .trim();

export const padToKbMinLength = (text: string): string => {
  if (text.length >= KB_MIN_CONTENT_LENGTH) return text;
  return `${text}\n\n${KB_CONTENT_PADDING.slice(0, KB_MIN_CONTENT_LENGTH - text.length)}`;
};

export const parseParamAttachments = (raw?: string): PickedFile[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return parsed.map((att: PickedFileLoose) => ({
      uri: att.url ?? att.attachmentURL,
      fileName: att.name,
      name: att.name,
      mimeType: att.mimeType,
      size: att.size,
      attachmentID: att.id ?? att.attachmentID,
      attachmentURL: att.url ?? att.attachmentURL,
      type: att.mimeType?.startsWith("image/") ? "image" : "file",
    }));
  } catch {
    return [];
  }
};

export const isRemoteFile = (file: PickedFile): boolean =>
  !!(
    file &&
    "uri" in file &&
    (file as PickedFileLoose).uri?.startsWith("http")
  );

export const isValidBlocksUuid = (id: string): boolean =>
  !id.startsWith("http") && BLOCKS_UUID_REGEX.test(id.trim());

export const isKbSupportedAttachment = (att: MergedAttachment): boolean =>
  KB_SUPPORTED_MIME_TYPES.has(att.mimeType);

export const normalizeExistingAttachment = (
  file: PickedFile,
  index: number,
): MergedAttachment => {
  const f = file as PickedFileLoose;
  return {
    id: f.attachmentID ?? f.id ?? `att-existing-${index}`,
    name: f.name ?? f.fileName ?? "attachment",
    size: f.size != null ? `${f.size}` : "0",
    url: f.uri ?? f.attachmentURL ?? f.url ?? "",
    mimeType: f.mimeType ?? "",
  };
};

export const buildKbText = (title: string, content: string): string => {
  const normalized = stripHtmlAndNormalize(content);
  return `# ${title.trim()}\n\n${padToKbMinLength(normalized)}`;
};

export const filterValidKbFileIds = (
  attachments: MergedAttachment[],
): string[] =>
  attachments
    .filter(
      (att) =>
        att?.id &&
        typeof att.id === "string" &&
        isValidBlocksUuid(att.id) &&
        isKbSupportedAttachment(att),
    )
    .map((att) => att.id.trim());

export const getAttachmentMeta = (
  file: AttachmentInput,
  index: number,
): { uri: string; mimeType: string; rawFileName: string } => {
  const f = file as PickedFileLoose;
  return {
    uri: f.uri ?? "",
    mimeType: f.mimeType ?? "application/octet-stream",
    rawFileName: f.fileName ?? f.name ?? `attachment-${index}`,
  };
};
